import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AlertCircle, ArrowRight, Shield } from "lucide-react";
import { ZestLogo } from "../components/ZestLogo";

export default function OpsLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role !== "super_admin") {
        setError("Access denied. Admin credentials required.");
        setLoading(false);
        return;
      }
      navigate("/ops");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6" data-testid="ops-login-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <ZestLogo height="h-9" className="brightness-0 invert" />
          <div className="h-6 w-px bg-slate-700" />
          <div className="flex items-center gap-1.5 bg-red-500/20 text-red-300 border border-red-500/30 px-2.5 py-1 rounded-full text-[11px] font-semibold">
            <Shield className="w-3 h-3" /> OPS
          </div>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-7">
          <h1 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Login</h1>
          <p className="text-sm text-slate-400 mb-6">Sign in to the operations panel</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 text-red-400 text-sm border border-red-500/20" data-testid="ops-login-error">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-300">Email</Label>
              <Input
                type="email" placeholder="admin@company.com"
                value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="ops-login-email"
                className="h-11 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-slate-300">Password</Label>
              <Input
                type="password" placeholder="Enter password"
                value={password} onChange={(e) => setPassword(e.target.value)} required
                data-testid="ops-login-password"
                className="h-11 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <Button type="submit" className="w-full h-11 text-sm font-semibold bg-red-600 hover:bg-red-700" disabled={loading} data-testid="ops-login-submit">
              {loading ? "Signing in..." : "Sign in to Ops"}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
